import React, { useState, useEffect } from 'react';
import { signIn, signOut, getCurrentUser } from 'aws-amplify/auth';
import { Link, useNavigate } from 'react-router-dom';

import Reset from '../../components/User/Reset';
import { useAuthStore } from '../../store/authStore';

function validateEmail(email) {
  // Remove leading/trailing spaces and any spaces inside
  email = email.trim().replace(/\s+/g, '');
  // Validate email format
  return /^[a-zA-Z0-9_\-\.]+@[a-zA-Z0-9\-]+\.[a-zA-Z]{1,5}$/.test(email);
}

type LoginProps = {
  onLoginSuccess?: () => void;
  onClose?: () => void;
  hideCloseButton?: boolean;
};

function Login({ onLoginSuccess, onClose, hideCloseButton = false }: LoginProps = {}) {
  const navigate = useNavigate();
  
  // Use the new AuthStore instead of Context API
  const { user, login, logout, isAuthenticating, authError } = useAuthStore();
  const isAuthenticated = !!user;
  
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isPassVisible, setIsPassVisible] = useState(false);
  const [reset, setReset] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !isLoading && !isAuthenticating) {
      if (onLoginSuccess) {
        onLoginSuccess();
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [isAuthenticated, navigate, onLoginSuccess, isLoading, isAuthenticating]);

  useEffect(() => {
    if (authError) {
      setErrorMessage(authError);
    }
  }, [authError]);

  // If user is already authenticated, show logout option instead of login form
  if (isAuthenticated) {
    return (
      <div className="container txt-contenido">
        <div className="login-container mb-5">
          <div className="signin-container">
            <div className="col-12 text-center">
              <h3 className="login-title" style={{ color: 'white' }}>
                Already Logged In
              </h3>
              <p style={{ color: 'white', marginBottom: '20px' }}>
                You are already authenticated. You can logout to test the login form.
              </p>
              <div className="container-contact100-form-btn">
                <div className="wrap-contact100-form-btn">
                  <div className="contact100-form-bgbtn" />
                  <button 
                    className="contact100-form-btn"
                    onClick={async () => {
                      await logout();
                      window.location.reload(); // Reload to reset state
                    }}
                  >
                    Logout
                  </button>
                </div>
              </div>
              <div style={{ marginTop: '20px' }}>
                <Link to="/" style={{ color: 'white' }}>
                  Go to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const togglePassVisibility = () => {
    setIsPassVisible(prevState => !prevState);
  };

  const handleChangeEmail = event => {
    const emailValue = event.target.value.trim();
    setEmail(emailValue);
    setIsEmailValid(validateEmail(emailValue));
  };

  const handleChangePassword = event => {
    const passwordValue = event.target.value;
    setPassword(passwordValue);
    setIsPasswordValid(passwordValue.length >= 8);
  };

  const handleSubmit = async evt => {
    evt.preventDefault();
    setErrorMessage('');
    
    // Simplified validation - just check if fields are not empty
    if (!email || !password) {
      setErrorMessage('Please enter both email and password');
      return;
    }
    
    try {
      setIsLoading(true);
      await login({ username: email, password });
      
      setTimeout(() => {
        if (onLoginSuccess) {
          onLoginSuccess();
        } else {
          navigate('/', { replace: true });
        }
      }, 100);
      
      setTimeout(() => {
        if (window.location.pathname === '/login') {
          window.location.href = '/';
        }
      }, 1000);
      
    } catch (err) {
      console.error('🔐 Login failed:', err);
      const typedError = err as { message: string };
      setErrorMessage(typedError.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = evt => {
    evt.preventDefault();
    setReset(true);
  };

  return (
    <div>
      <link rel="canonical" href="/login" />
      {reset ? (
        <Reset setReset={setReset} />
      ) : (
        <div className={`container txt-contenido ${isAnimating ? 'fade-out' : ''}`}>
          {(isLoading || isAuthenticating) && (
            <div className="loading-overlay">
              <div className="loading" />
            </div>
          )}
          <div className="login-container mb-5">
            <div className="signin-container">
              <div className="col-12">
                {!hideCloseButton && (
                  <button type="button" className="close" aria-label="Close">
                    {onClose ? (
                      <span
                        style={{ color: 'white', cursor: 'pointer' }}
                        onClick={onClose}
                        aria-hidden="true"
                      >
                        ×
                      </span>
                    ) : (
                      <Link style={{ color: 'white' }} to="/" aria-hidden="true">
                        ×
                      </Link>
                    )}
                  </button>
                )}
                <h3 className="login-title">SIGN IN</h3>
                <form className="validate-form" onSubmit={handleSubmit}>
                  <div className=" validate-input" data-validate="Email is required">
                    <label className="label-input100" style={{ color: 'white' }}>
                      Email:
                    </label>
                    <div className="line-input">
                      <input
                        className="form-input"
                        id="email"
                        type="text"
                        name="email"
                        value={email}
                        onChange={handleChangeEmail}
                        required
                      />
                    </div>
                  </div>
                  <div className="validate-input" data-validate="Password is required">
                    <div className="password-input">
                      <span className="label-input100" style={{ color: 'white' }}>
                        Password
                      </span>
                      <span className="eye-icon" onClick={togglePassVisibility}>
                        <i
                          className={`fa ${isPassVisible ? 'fa-eye' : 'fa-eye-slash'}`}
                          style={{ color: 'white' }}
                          aria-hidden="true"
                        />
                      </span>
                    </div>
                    <div className="line-input">
                      <input
                        onChange={handleChangePassword}
                        name="password"
                        id="password"
                        type={`${isPassVisible ? 'text' : 'password'}`}
                        className="input100"
                        placeholder="Password"
                      />
                    </div>
                    <span className="focus-input100 d-flex align-items-center justify-content-end"></span>
                  </div>
                  <div className="flex w-full justify-content-between">
                    <Link to="/sso" style={{ float: 'right', marginBottom: '20px', color: 'white' }}>
                      SSO Login
                    </Link>
                    <a
                      style={{ float: 'right', marginBottom: '20px', color: 'white' }}
                      href="true"
                      onClick={evt => {
                        handleReset(evt);
                      }}
                    >
                      Forgot Password
                    </a>
                  </div>

                  <div className="container-contact100-form-btn">
                    <div className="wrap-contact100-form-btn">
                      <div className="contact100-form-bgbtn" />
                      <button className="contact100-form-btn">Sign in</button>
                    </div>
                  </div>
                  {errorMessage ? <p className="text-center">{errorMessage}</p> : null}
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
